import React from 'react';
import { RefreshCw } from 'lucide-react';
import { AxiosError } from 'axios';
import { ButtonV2 } from '@pulsarml/avalon-ui';
import { Translate } from '@utils/i18n';
import { clearAppStorage } from '_clean-code/utils/storage';

const clearStorageAndRefresh = () => {
  // Presented to the user as "sign out", so the analytics identity goes too.
  clearAppStorage({ keepAnalyticsIdentity: false });
  window.location.reload();
};

const OutdatedVersionPage = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-screen tw-bg-gray-50">
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-10 tw-flex tw-flex-col tw-items-center tw-max-w-md tw-w-full tw-mx-4">
      <div className="tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-w-14 tw-h-14 tw-shrink-0 tw-mb-6">
        <RefreshCw className="tw-text-gray-500 tw-w-7 tw-h-7" />
      </div>
      <h1 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-3 tw-text-center">
        <Translate
          value="outdated_version_title"
          defaultValue="Update Required"
        />
      </h1>
      <p className="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-8 tw-leading-relaxed">
        <Translate
          value="outdated_version_description"
          defaultValue="A new version of this app is available. Please refresh the page to get the latest version and continue working."
        />
      </p>
      <ButtonV2
        variant="primary"
        size="default"
        className="tw-w-full tw-mb-6"
        onClick={() => window.location.reload()}
      >
        <Translate value="refresh_page" defaultValue="Refresh Page" />
      </ButtonV2>
      {import.meta.env.DEV && (
        <div className="tw-w-full tw-mb-6 tw-p-3 tw-rounded-md tw-border tw-border-yellow-300 tw-bg-yellow-50 tw-text-xs tw-text-yellow-900">
          <strong>Dev hint:</strong> the backend rejected this client as
          outdated. You may need to bump <code>VITE_APP_VERSION</code> in your
          local env to a value at or above the backend&apos;s minimum supported
          version.
        </div>
      )}
      <div className="tw-w-full tw-border-t tw-border-gray-100 tw-pt-6">
        <p className="tw-text-sm tw-text-gray-500 tw-leading-relaxed tw-mb-4">
          <Translate
            value="outdated_version_cookie_hint"
            defaultValue="If refreshing doesn't work, try signing out completely. This clears your local session data and forces the app to reload fresh."
          />
        </p>
        <ButtonV2
          variant="outline"
          size="default"
          className="tw-w-full"
          onClick={clearStorageAndRefresh}
        >
          <Translate
            value="clear_storage_and_refresh"
            defaultValue="Sign Out & Refresh"
          />
        </ButtonV2>
      </div>
    </div>
  </div>
);

// Authenticated user with zero company memberships. Recoverable — never a
// permanent blank screen: retry (re-fetch) or sign out.
export const NoCompanyAccessPage = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-screen tw-bg-gray-50">
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-10 tw-flex tw-flex-col tw-items-center tw-max-w-md tw-w-full tw-mx-4">
      <h1 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-3 tw-text-center">
        <Translate
          value="no_company_access_title"
          defaultValue="No company access"
        />
      </h1>
      <p className="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-8 tw-leading-relaxed">
        <Translate
          value="no_company_access_description"
          defaultValue="Your account isn't a member of any company yet. Ask your Pulsar administrator for access, then retry."
        />
      </p>
      <ButtonV2
        variant="primary"
        size="default"
        className="tw-w-full tw-mb-3"
        onClick={() => window.location.reload()}
      >
        <Translate value="retry" defaultValue="Retry" />
      </ButtonV2>
      <ButtonV2
        variant="outline"
        size="default"
        className="tw-w-full"
        onClick={clearStorageAndRefresh}
      >
        <Translate value="sign_out" defaultValue="Sign out" />
      </ButtonV2>
    </div>
  </div>
);

export const ErrorPage = ({ error }: { error: Error | null }) => {
  const axiosError = error as AxiosError | null;
  if (axiosError?.response?.status === 426) {
    return <OutdatedVersionPage />;
  }

  return (
    <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
      <div className="tw-bg-white tw-p-8 tw-rounded-md tw-shadow-md tw-flex tw-flex-col tw-items-center">
        <div className="tw-text-red-600 tw-text-2xl tw-mb-4">
          <Translate value="error" defaultValue="Error" />
        </div>
        <div className="tw-text-gray-700 tw-mb-2">
          <Translate
            value="failed_to_load_data"
            defaultValue="Failed to load user profile or company data. Please try refreshing the page."
          />
        </div>
        {error && (
          <div className="tw-text-xs tw-text-gray-600 tw-mb-2">
            {error.message ?? String(error)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage;
